// react
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { PlanningActivity } from '../../../../@types';

// context
import { ContextBanner, ContextUser } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import { Box, Divider, Menu, MenuItem, Paper, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../../../dialogs/DialogActions/DialogActions';

// icons
import {
	AccountTree as IconActivity,
	Add as IconAdd,
	Download as IconDownload,
	FormatListBulleted as IconList,
	UploadFile as IconUpload,
	ViewModule as IconGroup,
} from '@mui/icons-material';

// local components
import { ActivitiesDataTable } from './ActivitiesDataTable';
import {
	ActivityGroupedList,
	type ActivityGroupedListHandle,
	type GroupByKey,
} from '../../../ui/ActivityGroupedList/ActivityGroupedList';
import { TabSelector } from '../../../ui/TabSelector/TabSelector';

// components

// design system
import { spacing } from '../../../../utils/designSystem';

// v2 components
import { CustomFieldsLink } from '../../../ui/CustomFieldsLink/CustomFieldsLink';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { HelperText } from '../../../ui/HelperText/HelperText';

// dialogs
import {
	DialogBulkUpload,
	v2ActivitySchema,
	downloadTemplate,
	schemaWithCustomFields,
	LookupData,
} from '../../../dialogs/DialogBulkUpload';
import { DialogAssignActivities } from '../../../dialogs/DialogAssignActivities/DialogAssignActivities';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';
import { Empty } from '../../../ui/Empty/Empty';

// hooks
import { useFetch } from '../../../../hooks/useFetch';
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

// types
import { Category, Component, Entity, ExpenseType } from '../../../../@types';

// utils

export const Activities = () => {
	// hooks
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const {
		activities,
		deactivateActivity,
		deleteActivity,
		getContractsByActivity,
		loading,
		setFilterQuery,
		refetchActivities,
	} = useContext(ContextSpendPlan);

	// params
	const paramDialog = searchParams.get('dialog');
	const filterWorkPlan = searchParams.get('workPlan');
	const filterStatus = searchParams.get('status') || 'ALL';
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'name';
	const filterOrder = (searchParams.get('order') || 'ASC') as 'ASC' | 'DESC';
	const searchQuery = searchParams.get('search') || '';

	// Sync URL params with context filter query
	useEffect(() => {
		setFilterQuery('activities', {
			...(filterWorkPlan && { workPlan: parseInt(filterWorkPlan) }),
			...(filterStatus !== 'ALL' && { status: filterStatus }),
			...(searchQuery && { search: searchQuery }),
			order: filterOrder,
			orderBy: filterOrderBy,
		});
	}, [filterWorkPlan, filterStatus, filterOrderBy, filterOrder, searchQuery, setFilterQuery]);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const authHeaders = {
		Authorization: `Bearer ${accessToken}`,
		User: String(idToken),
	};

	// lookup data for bulk upload
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);

	useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setCategories(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&fields=id,name,status&status=ACTIVE`,
	});

	useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setComponents(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&fields=id,name,status&status=ACTIVE`,
	});

	useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				// ABAC: filter entities to user's scope (WBG users see all)
				const filtered =
					!user?.isWbg && user?.abac?.entities?.length
						? res.data.filter((e: Entity) => user.abac.entities.includes(e.id))
						: res.data;
				setEntities(filtered);
			}
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,type&type=PIU&type=SUB_PIU`,
	});

	useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setExpenseTypes(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&fields=id,name,status&status=ACTIVE&orderBy=name&order=ASC`,
	});

	// Work plans for bulk-upload relation resolution, fetched as the full project
	// universe (no page limit) so the picker lists every work plan regardless of
	// how the Work Plans listing happens to be paginated. Entities, components,
	// categories and expense types are already fetched in full above, and the
	// activities context on this page is unpaginated.
	const workPlanLookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: paramDialog === 'bulkUpload',
		workPlans: true,
	});
	const lookupData: LookupData = useMemo(
		() => ({
			entities: entities.map((e) => ({ id: e.id, name: e.name })),
			workPlans: workPlanLookups.workPlans,
			components: components.map((c) => ({ id: c.id, name: c.name })),
			categories: categories.map((c) => ({ id: c.id, name: c.name })),
			expenseTypes: expenseTypes.map((et) => ({ id: et.id, name: et.name })),
			activities: activities.map((a) => ({ id: a.id, name: a.name })),
		}),
		[entities, workPlanLookups.workPlans, components, categories, expenseTypes, activities]
	);

	// state
	const [activityMenuAnchor, setActivityMenuAnchor] = useState<null | HTMLElement>(null);
	const [menuActivity, setMenuActivity] = useState<PlanningActivity | null>(null);
	// Confirmation state for the deactivate action.
	const [activityPendingDeactivate, setActivityPendingDeactivate] =
		useState<PlanningActivity | null>(null);
	const [isDeactivatingActivity, setIsDeactivatingActivity] = useState(false);
	// Confirmation state for the hard-delete (PLANNED + unlinked) row action.
	const [activityPendingHardDelete, setActivityPendingHardDelete] =
		useState<PlanningActivity | null>(null);
	const [isHardDeletingActivity, setIsHardDeletingActivity] = useState(false);
	const [viewMode, setViewMode] = useState<'grouped' | 'table'>('table');
	const groupedListRef = useRef<ActivityGroupedListHandle>(null);
	const [assignTarget, setAssignTarget] = useState<{
		id: number;
		name: string;
		field: 'componentId' | 'categoryId' | 'workPlan';
	} | null>(null);

	// handlers for pagination and sorting
	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1'); // Reset to first page when changing rows per page
			return params;
		});
	};

	const handleSortChange = (orderBy: string, order: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', orderBy);
			params.set('order', order);
			return params;
		});
	};

	// handlers for filters
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			params.set('page', '1'); // Reset to first page on search
			return params;
		});
	};

	const handleFilterChange = (key: string, value: string | number) => {
		setSearchParams((params) => {
			if (key === 'status') {
				if (value === 'ALL') {
					params.delete('status');
				} else {
					params.set('status', String(value));
				}
			} else if (key === 'workPlan') {
				if (value === 'ALL') {
					params.delete('workPlan');
				} else {
					params.set('workPlan', String(value));
				}
			}
			params.set('page', '1'); // Reset to first page on filter change
			return params;
		});
	};

	// handlers
	const handleBulkUploadFile = () => {
		searchParams.set('dialog', 'bulkUpload');
		setSearchParams(searchParams);
	};

	const handleDownloadTemplate = async () => {
		try {
			await downloadTemplate({
				schema: schemaWithCustomFields(v2ActivitySchema, project?.customFieldDefinitions ?? []),
				lookupData,
			});
			setBanner({ message: 'Template downloaded', severity: 'success' });
		} catch {
				setBanner({ message: t('spendPlan.activities.failedToGenerateTemplate'), severity: 'error' });
		}
	};

	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogWorkPlan');
			params.delete('dialogActivity');
			params.delete('dialogParentActivity');
			return params;
		});
	};

	const handleOpenActivityPage = (activityId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/activity/${activityId}`);
	};

	const handleOpenContractPage = (contractId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/contract/${contractId}`);
	};

	const handleOpenAddDialog = (workPlanId?: number) => {
		searchParams.set('dialog', 'addActivity');
		if (workPlanId) {
			searchParams.set('dialogWorkPlan', String(workPlanId));
		}
		setSearchParams(searchParams);
	};

	const handleGroupedAddActivity = (key: GroupByKey, itemId: number) => {
		const next = new URLSearchParams(searchParams);
		next.set('dialog', 'addActivity');
		if (key === 'component') next.set('dialogComponent', String(itemId));
		if (key === 'category') next.set('dialogCategory', String(itemId));
		if (key === 'workPlan') next.set('dialogWorkPlan', String(itemId));
		setSearchParams(next);
	};

	const handleGroupedAssignExisting = (key: GroupByKey, itemId: number, itemName: string) => {
		const fieldMap: Record<string, 'componentId' | 'categoryId' | 'workPlan'> = {
			component: 'componentId',
			category: 'categoryId',
			workPlan: 'workPlan',
		};
		setAssignTarget({ id: itemId, name: itemName, field: fieldMap[key] });
	};

	const handleActivityMenuClose = () => {
		setActivityMenuAnchor(null);
		setMenuActivity(null);
	};

	const handleDeactivateActivity = async () => {
		if (!menuActivity) return;
		// Stash the activity before the menu closes — closing the menu nulls
		// menuActivity, and the user still needs to confirm in the dialog.
		setActivityPendingDeactivate(menuActivity);
		handleActivityMenuClose();
	};

	const handleConfirmDeactivateActivity = async () => {
		if (!activityPendingDeactivate) return;
		setIsDeactivatingActivity(true);
		const response = await deactivateActivity(activityPendingDeactivate.id);
		setIsDeactivatingActivity(false);

		if (!response.error) {
			setBanner({
				message: `Activity "${activityPendingDeactivate.name}" deactivated.`,
				severity: 'success',
			});
		} else {
			setBanner({
					message: t('spendPlan.activities.failedToDeactivate', { message: response.error.message }),
				severity: 'error',
			});
		}

		setActivityPendingDeactivate(null);
	};

	const handleDeleteDraftActivity = (activity: PlanningActivity) => {
		setActivityPendingHardDelete(activity);
	};

	const handleConfirmHardDeleteActivity = async () => {
		if (!activityPendingHardDelete) return;
		setIsHardDeletingActivity(true);
		const response = await deleteActivity(activityPendingHardDelete.id);
		setIsHardDeletingActivity(false);

		if (!response.error) {
			setBanner({
				message: `Activity "${activityPendingHardDelete.name}" deleted.`,
				severity: 'success',
			});
			// Close any dialog still pointed at the deleted activity.
			setSearchParams((params) => {
				if (params.get('dialogActivity') === String(activityPendingHardDelete.id)) {
					params.delete('dialog');
					params.delete('dialogActivity');
				}
				if (params.get('dialogParentActivity') === String(activityPendingHardDelete.id)) {
					params.delete('dialogParentActivity');
				}
				return params;
			});
		} else {
			const hint = (response.error as { details?: { hint?: string } }).details?.hint;
			setBanner({
					message: t('spendPlan.activities.failedToDelete', { message: `${response.error.message}${hint ? ` ${hint}` : ''}` }),
				severity: 'error',
			});
		}

		setActivityPendingHardDelete(null);
	};

	const handleOpenEditActivityDialog = () => {
		if (!menuActivity) return;
		searchParams.set('dialog', 'editActivity');
		searchParams.set('dialogActivity', String(menuActivity.id));
		setSearchParams(searchParams);
		handleActivityMenuClose();
	};

	const handleOpenAddSubActivityDialog = () => {
		if (!menuActivity) return;
		// Get the work plan ID from the menu activity
		const workPlanId =
			typeof menuActivity.workPlan === 'number' ? menuActivity.workPlan : menuActivity.workPlan?.id;
		searchParams.set('dialog', 'addSubActivity');
		searchParams.set('dialogParentActivity', String(menuActivity.id));
		if (workPlanId) {
			searchParams.set('dialogWorkPlan', String(workPlanId));
		}
		setSearchParams(searchParams);
		handleActivityMenuClose();
	};

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconActivity}
				title={t('spendPlan.activities')}
					subtitle={t('spendPlan.activities.subtitle')}
				variant="activity"
				secondaryBadge={<CustomFieldsLink entity="activity" />}
				actions={
					<BtnSplit
						actions={[
							{
								children: t('spendPlan.addActivity'),
								onClick: () => handleOpenAddDialog(),
								startIcon: <IconAdd />,
							},
							{
								children: t('spendPlan.bulkUploadFromFile'),
								onClick: handleBulkUploadFile,
								startIcon: <IconUpload />,
							},
							{
								children: t('spendPlan.downloadTemplate'),
								onClick: handleDownloadTemplate,
								startIcon: <IconDownload />,
							},
						]}
					/>
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* View Mode Toggle */}
				<Box sx={{ mb: spacing.md }}>
					<TabSelector
						value={viewMode}
						onChange={setViewMode}
						options={[
							{
								value: 'table',
								label: t('spendPlan.activities.allActivities'),
								icon: <IconList sx={{ fontSize: { xs: 16, sm: 18 }, mr: 0.75 }} />,
							},
							{
								value: 'grouped',
								label: t('spendPlan.activities.grouped'),
								icon: <IconGroup sx={{ fontSize: { xs: 16, sm: 18 }, mr: 0.75 }} />,
							},
						]}
					/>
				</Box>

				{/* Table View */}
				{viewMode === 'table' && (
					<Box>
						<ActivitiesDataTable
							isLoading={loading.activities}
							onAddActivity={() => handleOpenAddDialog()}
							onAddSubActivity={(activity) => {
								const workPlanId =
									typeof activity.workPlan === 'number' ? activity.workPlan : activity.workPlan?.id;
								searchParams.set('dialog', 'addSubActivity');
								searchParams.set('dialogParentActivity', String(activity.id));
								if (workPlanId) {
									searchParams.set('dialogWorkPlan', String(workPlanId));
								}
								setSearchParams(searchParams);
							}}
							onDeactivateActivity={(activity) => {
								setMenuActivity(activity);
								handleDeactivateActivity();
							}}
							onDeleteDraftActivity={handleDeleteDraftActivity}
							onEditActivity={(activity) => {
								searchParams.set('dialog', 'editActivity');
								searchParams.set('dialogActivity', String(activity.id));
								setSearchParams(searchParams);
							}}
							onLinkContract={(activity) => {
								searchParams.set('dialog', 'linkContract');
								searchParams.set('dialogActivity', String(activity.id));
								setSearchParams(searchParams);
							}}
							onSortChange={handleSortChange}
							order={filterOrder}
							orderBy={filterOrderBy}
							page={parseInt(filterPage)}
							rowsPerPage={parseInt(filterLimit)}
							totalCount={activities.length}
							onPageChange={handlePageChange}
							onRowsPerPageChange={handleRowsPerPageChange}
							searchValue={searchQuery}
							onSearchChange={handleSearchChange}
							filterValues={{
								status: filterStatus || 'ALL',
								workPlan: filterWorkPlan ? parseInt(filterWorkPlan) : 'ALL',
							}}
							onFilterChange={handleFilterChange}
						/>
					</Box>
				)}

				{/* Grouped View - ActivityGroupedList with Component/Category tabs */}
				{viewMode === 'grouped' && !loading.activities && activities.length > 0 && (
					<ActivityGroupedList
						ref={groupedListRef}
						tabs={[
							{ key: 'component', label: t('spendPlan.workPlan.byComponent') },
							{ key: 'category', label: t('spendPlan.workPlan.byCategory') },
							{ key: 'workPlan', label: t('spendPlan.activities.byWorkPlan') },
						]}
						onNavigateActivity={(id: number) => handleOpenActivityPage(id)}
						onNavigateContract={handleOpenContractPage}
						getContractsByActivity={getContractsByActivity}
						onAddActivity={handleGroupedAddActivity}
						onAssignExisting={handleGroupedAssignExisting}
					/>
				)}

				{/* Empty/Loading state for Grouped View */}
				{viewMode === 'grouped' && (activities.length === 0 || loading.activities) && (
					<Paper sx={{ mt: 2, p: 6 }}>
						<Empty
							isLoading={loading.activities}
							title={{
								children: t('spendPlan.empty.activity.title'),
							}}
							content={{
								children:
									searchQuery || filterWorkPlan || filterStatus !== 'ALL'
										? t('spendPlan.tryAdjustingFilters')
										: t('spendPlan.empty.activity.content'),
							}}
							actions={[
								{
									children: t('addActivity'),
									onClick: () => handleOpenAddDialog(),
									variant: 'contained',
								},
							]}
						/>
					</Paper>
				)}

				{/* Help text */}
				<HelperText
					title={t('spendPlan.activities.helperTitle')}
					description={t('spendPlan.activities.helperDescription')}
					icon={<IconActivity sx={{ color: '#fff', fontSize: { xs: 18, sm: 20 } }} />}
				/>
			</Box>

			{/* Assign Existing Activities Dialog */}
			<DialogAssignActivities
				activities={activities}
				assignField={assignTarget?.field || 'componentId'}
				assignToId={assignTarget?.id || 0}
				assignToName={assignTarget?.name || ''}
				isOpen={Boolean(assignTarget)}
				onClose={() => setAssignTarget(null)}
				onSuccess={() => setAssignTarget(null)}
			/>

			{/* Bulk Upload Activities Dialog */}
			<DialogBulkUpload
				isOpen={paramDialog === 'bulkUpload'}
				onClose={handleCloseDialog}
				schema={v2ActivitySchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				customFieldDefinitions={project?.customFieldDefinitions}
				onSuccess={() => {
					refetchActivities();
					setBanner({ message: t('spendPlan.activities.uploadedSuccess'), severity: 'success' });
				}}
			/>

			{/* Activity Actions Menu */}
			<Menu
				anchorEl={activityMenuAnchor}
				open={Boolean(activityMenuAnchor)}
				onClose={handleActivityMenuClose}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
				<MenuItem onClick={handleOpenEditActivityDialog}>{t('spendPlan.activities.editActivity')}</MenuItem>
				<MenuItem onClick={handleOpenAddSubActivityDialog}>{t('spendPlan.activities.addSubActivity')}</MenuItem>
				<MenuItem onClick={handleActivityMenuClose}>{t('spendPlan.activities.linkContract')}</MenuItem>
				<Divider />
				<MenuItem onClick={handleDeactivateActivity} sx={{ color: 'error.main' }}>
					{t('spendPlan.activities.deactivateActivity')}
				</MenuItem>
			</Menu>

			{/* Deactivate Activity Confirmation */}
			<DialogActions
				isOpen={activityPendingDeactivate !== null}
				onClose={() => !isDeactivatingActivity && setActivityPendingDeactivate(null)}
				maxWidth={480}
				title={{ children: t('spendPlan.activities.deactivateDialogTitle') }}
				subtitle={{
					children: t('spendPlan.activities.deactivateDialogSubtitle', { name: activityPendingDeactivate?.name ?? '' }),
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						disabled: isDeactivatingActivity,
						onClick: () => setActivityPendingDeactivate(null),
						variant: 'text',
					},
					{
						children: isDeactivatingActivity ? t('spendPlan.activities.deactivating') : t('spendPlan.activities.deactivateActivity'),
						color: 'error',
						disabled: isDeactivatingActivity,
						loading: isDeactivatingActivity,
						onClick: handleConfirmDeactivateActivity,
						variant: 'contained',
					},
				]}>
				<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
					{t('spendPlan.activities.deactivateDialogBody')}
				</Typography>
			</DialogActions>

			{/* Delete Draft Activity Confirmation */}
			<DialogActions
				isOpen={activityPendingHardDelete !== null}
				onClose={() => !isHardDeletingActivity && setActivityPendingHardDelete(null)}
				maxWidth={520}
				title={{ children: t('spendPlan.activities.deleteDialogTitle') }}
				subtitle={{
					children: t('spendPlan.activities.deleteDialogSubtitle', { name: activityPendingHardDelete?.name ?? '' }),
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						disabled: isHardDeletingActivity,
						onClick: () => setActivityPendingHardDelete(null),
						variant: 'text',
					},
					{
						children: isHardDeletingActivity ? t('spendPlan.activities.deleting') : t('spendPlan.activities.deleteActivity'),
						color: 'error',
						disabled: isHardDeletingActivity,
						loading: isHardDeletingActivity,
						onClick: handleConfirmHardDeleteActivity,
						variant: 'contained',
					},
				]}>
				<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
					{t('spendPlan.activities.deleteDialogBody')}
				</Typography>
			</DialogActions>
		</Box>
	);
};
